import React from 'react';

import {
	actions,
	ensurePluginOrder,
	useGetLatest,
	TableState,
	ActionType,
	Row,
	MetaBase,
	TableInstance,
	Hooks,
	makePropGetter
} from 'react-table';
import type {
	TableToggleAllGroupsSelectedProps,
	TableToggleGroupSelectedProps
} from '../../../types/react-table-config';

export enum RowSelectedState {
	SELECTED = 'SELECTED',
	NOT_SELECTED = 'NOT_SELECTED',
	SOME_SELECTED = 'SOME_SELECTED'
}

const pluginName = 'useGroupSelect';

// Actions
actions.toggleAllGroupsSelected = 'toggleAllGroupsSelected';
actions.toggleGroupSelected = 'toggleGroupSelected';

export const useGroupSelect = <D extends object = {}>(hooks: Hooks<D>) => {
	hooks.getToggleGroupSelectedProps = [defaultGetToggleGroupSelectedProps];
	hooks.getToggleAllGroupsSelectedProps = [defaultGetToggleAllGroupsSelectedProps];
	hooks.stateReducers.push(reducer);
	hooks.useInstance.push(useInstance);
	hooks.prepareRow.push(prepareRow);
};

useGroupSelect.pluginName = pluginName;

const defaultGetToggleGroupSelectedProps = <D extends object = {}>(
	props: TableToggleGroupSelectedProps,
	{ instance, row }: { instance: TableInstance<D>; row: Row }
) => {
	const { manualRowSelectedKey = 'isSelected' } = instance;
	let checked = false;

	if (row.original && row.original[manualRowSelectedKey]) {
		checked = true;
	} else {
		checked = row.isSelected;
	}

	return [
		props,
		{
			onChange: e => {
				row.toggleGroupSelected(e.target.checked);
			},
			style: {
				cursor: 'pointer'
			},
			checked,
			indeterminate: row.isSomeSelected
		}
	];
};

const defaultGetToggleAllGroupsSelectedProps = <D extends object = {}>(
	props: TableToggleAllGroupsSelectedProps,
	{ instance }: MetaBase<D>
) => [
	props,
	{
		onChange: e => {
			instance.toggleAllGroupsSelected(e.target.checked);
		},
		style: {
			cursor: 'pointer'
		},
		checked: instance.isAllGroupsSelected,
		indeterminate: Boolean(!instance.isAllGroupsSelected && Object.keys(instance.state.selectedGroupIds).length)
	}
];

function reducer<D extends object = {}>(
	state: TableState<D>,
	action: ActionType,
	previousState: TableState<D>,
	instance: TableInstance<D>
) {
	if (action.type === actions.init) {
		return {
			selectedGroupIds: {},
			...state
		};
	}

	if (action.type === actions.toggleAllGroupsSelected) {
		return toggleAllGroupsSelectedAction(action, instance, state);
	}

	if (action.type === actions.toggleGroupSelected) {
		return toggleGroupSelectedAction(action, instance, state);
	}

	return state;
}

function useInstance<D extends object = {}>(instance: TableInstance<D>) {
	const {
		getHooks,
		plugins,
		rowsById,
		onlyGroupedRowsById = rowsById,
		state: { selectedGroupIds, groupBy },
		dispatch,
		getSubRows
	} = instance;

	ensurePluginOrder(
		plugins,
		['useFilters', 'useGroupBy', 'useSortBy', 'useExpanded', 'usePagination', 'useRowSelect'],
		'useGroupSelect'
	);

	Object.values(onlyGroupedRowsById).forEach(row => {
		const selectionState = getRowSelectedState(row, selectedGroupIds, getSubRows);

		row.isSelected =
			selectionState !== RowSelectedState.SOME_SELECTED && selectionState !== RowSelectedState.NOT_SELECTED;
		row.isSomeSelected = selectionState === RowSelectedState.SOME_SELECTED;
	});

	const lastGroupByKey = groupBy[groupBy.length - 1];

	let isAllGroupsSelected = false;

	if (Object.keys(selectedGroupIds).length) {
		const isSomeGroupsSelected = Object.keys(onlyGroupedRowsById)
			.filter(rowId => onlyGroupedRowsById[rowId].groupByID === lastGroupByKey)
			.some(rowId => {
				const key = getKey(onlyGroupedRowsById[rowId]);

				return !selectedGroupIds[key];
			});

		if (!isSomeGroupsSelected) {
			isAllGroupsSelected = true;
		}
	}

	const toggleAllGroupsSelected = React.useCallback(
		value => dispatch({ type: actions.toggleAllGroupsSelected, value }),
		[dispatch]
	);

	const toggleGroupSelected = React.useCallback(
		(id, value) => dispatch({ type: actions.toggleGroupSelected, id, value }),
		[dispatch]
	);

	const getInstance = useGetLatest(instance);

	// @ts-ignore
	const getToggleAllGroupsSelectedProps = makePropGetter(getHooks().getToggleAllGroupsSelectedProps, {
		instance: getInstance()
	});

	Object.assign(instance, {
		isAllGroupsSelected,
		toggleGroupSelected,
		toggleAllGroupsSelected,
		getToggleAllGroupsSelectedProps
	});
}

function prepareRow<D extends object = {}>(row: Row<D>, { instance }: MetaBase<D>) {
	if (row.isGrouped) {
		row.toggleGroupSelected = set => instance.toggleGroupSelected(row.id, set);

		// @ts-ignore
		row.getToggleGroupSelectedProps = makePropGetter(instance.getHooks().getToggleGroupSelectedProps, {
			instance: instance,
			row
		});
	}
}

function getRowSelectedState(row, selectedGroupIds, getSubRows): RowSelectedState {
	const key = getKey(row);

	if (selectedGroupIds[key]) {
		return RowSelectedState.SELECTED;
	}

	const subRows = getSubRows(row);

	if (subRows && subRows.length && subRows[0].isGrouped) {
		let allChildrenSelected = true;
		let someSelected = false;

		subRows.forEach(subRow => {
			// Bail out early if we know both of these
			if (someSelected && !allChildrenSelected) {
				return;
			}

			if (getRowSelectedState(subRow, selectedGroupIds, getSubRows) === RowSelectedState.SELECTED) {
				someSelected = true;
			} else {
				allChildrenSelected = false;
			}
		});

		return allChildrenSelected
			? RowSelectedState.SELECTED
			: someSelected
			? RowSelectedState.SOME_SELECTED
			: RowSelectedState.NOT_SELECTED;
	}

	return RowSelectedState.NOT_SELECTED;
}

const getKey = row => `${row.groupByVal}`;

///////////////////////////////////////////////
// actions
///////////////////////////////////////////////

const toggleAllGroupsSelectedAction = <D extends object = {}>(
	action: ActionType,
	instance: TableInstance<D>,
	state: TableState<D>
) => {
	const { value: setSelected } = action;
	const { isAllGroupsSelected, rowsById, onlyGroupedRowsById = rowsById } = instance;

	const selectAll = typeof setSelected !== 'undefined' ? setSelected : !isAllGroupsSelected;

	// Only remove/add the rows that are visible on the screen
	//  Leave all the other rows that are selected alone.
	const selectedGroupIds = Object.assign({}, state.selectedGroupIds);
	const lastGroupByKey = state.groupBy[state.groupBy.length - 1];

	const updateSelectedList = (selectAll: boolean) => {
		Object.keys(onlyGroupedRowsById)
			.filter(rowId => onlyGroupedRowsById[rowId].groupByID === lastGroupByKey)
			.forEach(rowId => {
				const key = getKey(onlyGroupedRowsById[rowId]);
				if (selectAll) {
					selectedGroupIds[key] = true;
				} else {
					delete selectedGroupIds[key];
				}
			});
	};

	updateSelectedList(selectAll);

	return {
		...state,
		selectedGroupIds
	};
};

const toggleGroupSelectedAction = <D extends object = {}>(
	action: ActionType,
	instance: TableInstance<D>,
	state: TableState<D>
) => {
	const { id, value: setSelected } = action;
	const { rowsById, onlyGroupedRowsById = rowsById, getSubRows } = instance;
	const isSelected = state.selectedGroupIds[id];
	const shouldExist = typeof setSelected !== 'undefined' ? setSelected : !isSelected;

	if (isSelected === shouldExist) {
		return state;
	}

	const newSelectedGroupIds = Object.assign({}, state.selectedGroupIds);

	const handleRowById = id => {
		const row = onlyGroupedRowsById[id];
		const lastGroupByKey = state.groupBy[state.groupBy.length - 1];

		if (row?.isGrouped && row.groupByID === lastGroupByKey) {
			const key = getKey(row);
			if (shouldExist) {
				newSelectedGroupIds[key] = true;
			} else {
				delete newSelectedGroupIds[key];
			}
		}

		const subRows = getSubRows(row);

		if (row.groupByID !== lastGroupByKey && subRows && subRows[0].isGrouped) {
			return subRows.forEach(row => handleRowById(row.id));
		}
	};

	handleRowById(id);

	return {
		...state,
		selectedGroupIds: newSelectedGroupIds
	};
};
